import instantsearch from 'instantsearch.js';

import {hits, searchBox} from 'instantsearch.js/es/widgets/index';

function stripTag(text) {
  let div = document.createElement('div');
  div.innerHTML = text;
  return div.innerText;
}

const entityMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  '\'': '&#39;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
};

function escapeHtml(string) {
  return String(string).replace(/[&<>"'`=/]/g, function (s) {
    return entityMap[s];
  });
}

function fuzzysearch(needle, haystack) {
  const hlen = haystack.length;
  const nlen = needle.length;
  if (nlen > hlen) {
    return false;
  }
  if (nlen === hlen) {
    return needle === haystack;
  }
  outer: for (var i = 0, j = 0; i < nlen; i++) {
    var nch = needle.charCodeAt(i);
    while (j < hlen) {
      if (haystack.charCodeAt(j++) === nch) {
        continue outer;
      }
    }
    return false;
  }
  return true;
}

interface KeyValuePair {
  key: string
  value: any

  [index: string]: any
}

function entries(obj): KeyValuePair[] {
  let ownProps = Object.keys(obj),
    i = ownProps.length,
    resArray = new Array(i);
  while (i--)
    resArray[i] = {key: ownProps[i], value: obj[ownProps[i]]};

  return resArray;
}

interface FacetEntry {
  label: string
  count: number
  url: string
}

interface CategoryFacetEntry extends FacetEntry {
  sort: number
}

interface AlgoliaSearchBoxOptions {
  appId: string,
  apiKey: string,
  indexName: string,
  elDropdown: HTMLDivElement,
  elSearchBox: HTMLInputElement,
  tmplPathProduct: string,
  tmplPathSearch: string,
  tmplPathProductImage: string,
  tmplPathProductNoImage: string,

  //optional options
  limitProductDescription?: number,
  limitFacetCategoryItems?: number,
  limitHitsPerPage?: number
}

// NOTICE: boost value a relative to product count in each rayon
const CATEGORY_BOOST = {
  'Bio': 200,
  'Engrais': 100,
  'Nutrition animale': 10,
  'Phyto': 30,
  'Semences': 50,
  'Élevage': 10,
  'GNR': 100,
};
const CATEGORY_SEPARATOR = ' > ';

export default class AlgoliaSearchBox {

  static init(element: HTMLElement) {
    if (!element) {
      return;
    }

    if (element['_searchBar']) {
      throw new Error('Error: searchBar already initialized on element');
    }

    const options = {
      appId: element.getAttribute('data-app-id'),
      apiKey: element.getAttribute('data-api-key'),
      indexName: element.getAttribute('data-index-name'),
      elDropdown: document.querySelector(element.getAttribute('data-cid-dropdown')) as HTMLDivElement,
      elSearchBox: element.querySelector('input[name="query"]') as HTMLInputElement,
      tmplPathProduct: element.getAttribute('data-tmpl-path-product'),
      tmplPathProductImage: element.getAttribute('data-tmpl-path-product-image'),
      tmplPathProductNoImage: element.getAttribute('data-tmpl-path-product-no-image'),
      tmplPathSearch: element.getAttribute('action'),
    };

    const searchBar = new AlgoliaSearchBox(element, options);

    element['_searchBar'] = searchBar;

    // trigger initial search when keydown
    element.addEventListener('keyup', function (e) {
      if (element.className.indexOf('ais-search-box') < 0) {
        element.className += ' ais-search-box';
        searchBar.render();
      }

      if (e.which === 27 /*ESC*/) {
        // esc to close dropdown
        searchBar.setActive(false);
      } else {
        // any time when user try to type in search bar, we active dropdown
        searchBar.setActive(true);
      }
    });

    // search box focus to active dropdown
    options.elSearchBox.addEventListener('focus', () => {
      searchBar.setActive(true);
    });

    // hide search box when page click
    element.querySelector('.backdrop-layer').addEventListener('click', (e: Event) => {
      if (!e.defaultPrevented) {
        searchBar.setActive(false);
      }
    });

    // stop propagation on click inside form
    element.querySelector('.main-layer').addEventListener('click', (e: Event) => {
      e.stopPropagation();
    });

    return searchBar;
  };

  private search;

  private elWidgetProducts: HTMLDivElement;

  private isStarted: boolean;

  private options: Partial<AlgoliaSearchBoxOptions>;

  constructor(private el: HTMLElement,
              options: AlgoliaSearchBoxOptions) {

    this.isStarted = false;
    this.options = {
      limitProductDescription: 100,
      limitFacetCategoryItems: 6,
      limitHitsPerPage: 4,
    };

    for (let key in options) if (options.hasOwnProperty(key)) {
      this.options[key] = options[key];
    }

    this.search = instantsearch({
      appId: options.appId,
      apiKey: options.apiKey,
      indexName: options.indexName,
      urlSync: false,
      limit: this.options.limitHitsPerPage, //only one page
      searchParameters: {
        facets: ['categories.lvl0', 'categories.lvl1', 'categories.lvl2', 'categories.lvl3', 'sparePartType'],
        facetFilters: [],
        hitsPerPage: this.options.limitHitsPerPage,
        facetingAfterDistinct: true,
        attributes: ['name', 'productId', 'slug', 'shortDescription', 'image', 'categories'],
      },
    });

    this.search.addWidget(this._createSearchBoxWidget());
    this.search.addWidget(this._createHitsWidget());

    // bind events
  }

  public setActive(active: boolean) {
    if (active) {
      if (this.el.className.indexOf(' active') < 0) {
        this.el.className += ' active';
      }
    } else {
      this.el.className = this.el.className.replace(' active', '');
    }
  }

  private _createSearchBoxWidget() {
    return searchBox({
      container: this.options.elSearchBox,
      placeholder: trans('front.javascript.algolia_search_box.search_box_widget_placeholder'),
      wrapInput: false,
    });
  }

  private _createHitsWidget() {
    this.elWidgetProducts = (() => {
      let div = document.createElement('div');
      this.options.elDropdown.appendChild(div);
      return div;
    })();

    return hits({
      container: this.elWidgetProducts,
      templates: {
        empty: trans('front.javascript.algolia_search_box.hits_widget_empty'),
        allItems: this._renderHits,
      },
    });
  }

  private _renderFacetSearchPageUrl = (query: string, attributeName: string, value: string | null) => {
    let url = this.options.tmplPathSearch + '?query=' + encodeURIComponent(query);

    if (value) {
      url += '&' + encodeURIComponent('refinementList[' + attributeName + '][0]') + '=' + encodeURIComponent(value);
    }

    return url;
  };

  private _renderFacet = (response: instantsearch.Response, options: {
    /**
     * (Required) attribute to make faceting, need also present in searchParameters.facets
     */
    attributeName: string,
    /**
     * Extra css class put on root
     */
    cssClass: string,
    /**
     * Limit number of item displayed in refinement list
     */
    limit: number,
    /**
     * Text to header
     */
    header: string,
  }) => {
    const query = response.query;
    const getFacetEntries = (facetName: string): FacetEntry[] => {
      const facet = response.facets.find(facet => facet.name === facetName);
      return facet ? entries(facet.data).map(data => ({
        label: data.key,
        count: data.value,
        url: this._renderFacetSearchPageUrl(query, facetName, data.key),
      })) : [];
    };

    let items = getFacetEntries(options.attributeName);

    // sort is mutator
    items.sort((a, b) => (b.count - a.count));

    // apply limit
    if (options.limit) {
      items = items.slice(0, options.limit);
    }

    const urlSeeAll = this._renderFacetSearchPageUrl(response.query, options.attributeName, null);
    const btnSeeAll = trans('front.javascript.algolia_search_box.ais_refinement_list_btn_see_all');

    return items.length > 0 ? `<div class="ais-refinement-list ${options.cssClass}">
<div class="ais-header">${options.header}<a class="link-see-all" href="${urlSeeAll}">${btnSeeAll}</a></div>
<div class="ais-refinement-list--list">
${items.map(this._renderFacetItem).join('')}
</div>
</div>` : '';
  };

  private _renderFacetItem = (item: FacetEntry) => {
    return `<div class="ais-refinement-list--item"><a href="${item.url}">${item.label} (${item.count})</a></div>`;
  };

  private _renderCategorySearchPageUrl = (query: string) => {
    let url = this.options.tmplPathSearch + '?query=' + encodeURIComponent(query);

    return url;
  };

  private _renderCategoryList = (response: instantsearch.Response) => {
    let categories: CategoryFacetEntry[] = [];
    const {facets, query} = response;

    // fetch facet data and convert to FacetEntry

    const cats: CategoryFacetEntry[][] = ['categories.lvl0', 'categories.lvl1', 'categories.lvl2', 'categories.lvl3']
      .map(key => facets.find(facet => facet.name === key))
      .map(facet => facet ? facet.data : [])
      .map(data => entries(data).map(entry =>
        ({
          label: entry.key,
          count: entry.value,
          url: this._renderCategorySearchPageUrl(query),
          sort: entry.value
            * (CATEGORY_BOOST[entry.key.split(CATEGORY_SEPARATOR)[0]] || 1)
            * (fuzzysearch(query.toLowerCase(), entry.key.toLowerCase()) ? 2 : 1),  // try boost a little if category words match
        }),
      ))
    ;

    for (let i = 0; i < cats.length; i++) {
      const j = i + 1;
      const icats: CategoryFacetEntry[] = cats[i];
      const jcats: CategoryFacetEntry[] = j < cats.length ? cats[j] : null;

      if (jcats) {
        // add only category not present on next level
        categories = categories.concat(...icats.filter(icat => jcats.findIndex(jcat => jcat.label.indexOf(icat.label) === 0) === -1));
      } else {
        // final level: add all categories
        categories = categories.concat(...icats);
      }
    }

    // ORDER BY category.value DESC LIMIT $MAX_CATEGORY_ITEMS
    categories = categories.sort((a, b) => b.sort - a.sort).slice(0, this.options.limitFacetCategoryItems);

    const urlSeeAll = this._renderCategorySearchPageUrl(query);
    const labelHeader = trans('front.javascript.algolia_search_box.label_facet_category_header');
    const btnSeeAll = trans('front.javascript.algolia_search_box.ais_refinement_list_btn_see_all');

    return categories.length > 0 ? `<div class="ais-refinement-list facet-category">
<div class="ais-header">${labelHeader}<a class="link-see-all" href="${urlSeeAll}">${btnSeeAll}</a></div>
<div class="ais-refinement-list--list">
${categories.map(this._renderCategoryListItem).join('')}
</div>
</div>
</div>` : '';
  };

  private _renderCategoryListItem = ({label, count, url}) => {
    let parts = label.split(CATEGORY_SEPARATOR);
    if (parts.length > 1) {
      parts = parts.slice(1);
    }
    parts[parts.length - 1] = '<strong>' + parts[parts.length - 1] + '</strong>';

    // SPEC: remove root category name to shorten display
    return `<div class="ais-refinement-list--item"><a href="${url}">${parts.join(CATEGORY_SEPARATOR)} (${count})</a></div>`;
  };

  private _renderProductList = ({hits}) => {

    const header = trans('front.javascript.algolia_search_box.label_hits_header');
    return `<div class="ais-hits two-column">
<div class="ais-header">${header}</div>
${hits.map(hit => this._renderProductItem(hit)).join('')}
</div>`;
  };

  private _renderProductItem = (hit: instantsearch.Hit) => {
    const productUrl = this.options.tmplPathProduct.replace('0000', hit.productId).replace('_slug_', hit.slug);
    const imageUrl = hit.image.indexOf('https://') === 0 ? hit.image : this.options.tmplPathProductImage.replace('__image__', hit.image);

    let desc = stripTag(hit.shortDescription || hit.description || '');
    if (desc.length > this.options.limitProductDescription) {
      desc = desc.substr(0, this.options.limitProductDescription) + '...';
    }

    return `<div class="ais-hits--item">
<a href="${productUrl}">
<img class="image" src="${imageUrl}">
<div class="content">
    <div class="name">${hit._highlightResult.name.value}</div>
    <div class="short-description">${desc}</div>
</div>
</a>
</div>`;
  };

  private _renderHits = (response: instantsearch.Response) => {
    return this._renderFacet(response, {
        attributeName: 'sparePartType',
        limit: 8,
        cssClass: 'two-column highlight',
        header: trans('front.javascript.algolia_search_box.hits_widget_header', {'{{ query }}': escapeHtml(response.query || '')}),
      })
      + this._renderCategoryList(response)
      + this._renderProductList(response);
  };

  public render() {
    if (!this.isStarted) {
      this.search.start();
      this.isStarted = true;
      this.options.elSearchBox.focus(); // try re-focus on input
    }
  }
}
